import { Schedule, ScheduleEntry } from '@ordering/types/Schedule';
import { Temporal } from 'temporal-polyfill';

const DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;

const dayToNumber = (dayName: string) => {
  return DAYS.indexOf(dayName as (typeof DAYS)[number]) + 1; // +1 because Temporal uses 1 (Monday) to 7 (Sunday)
};

/**
 * Given a specific datetime (e.g. a pre-order fulfilAt time), returns the
 * schedule entry whose day and time window contains that datetime,
 * or null if no entry matches.
 */
export const getScheduleEntryForDateTime = (
  schedule: Schedule,
  timezone: string,
  dateTimeISO: string
): ScheduleEntry | null => {
  const dateTime = Temporal.Instant.from(dateTimeISO).toZonedDateTimeISO(timezone);
  const targetDay = DAYS[dateTime.dayOfWeek - 1];
  const targetTime = dateTime.toPlainTime();

  const exactMatch = schedule.entries.find((entry) => {
    if (entry.day !== targetDay) return false;
    const entryStart = Temporal.PlainTime.from(entry.startTime);
    const entryEnd = Temporal.PlainTime.from(entry.endTime);
    return (
      Temporal.PlainTime.compare(targetTime, entryStart) >= 0 && Temporal.PlainTime.compare(targetTime, entryEnd) < 0
    );
  });

  return exactMatch ?? null;
};

export const getCurrentScheduleEntry = (
  schedule: Schedule,
  timezone: string
): {
  currentOrNextSchedule: ScheduleEntry;
  hours: {
    starts: string;
    ends: string;
    day: string;
  };
  isCurrentlyOpen: boolean;
  isOpenToday: boolean;
  isOpenTomorrow: boolean;
} => {
  const now = Temporal.Now.zonedDateTimeISO(timezone);
  const currentDayOfWeek = now.dayOfWeek;
  const currentTime = now.toPlainTime().toString();

  const sortedEntries = [...schedule.entries].sort((a, b) => {
    const dayOfWeekA = dayToNumber(a.day);
    const dayOfWeekB = dayToNumber(b.day);

    if (dayOfWeekA !== dayOfWeekB) {
      return dayOfWeekA - dayOfWeekB;
    }

    const startTimeA = Temporal.PlainTime.from(a.startTime);
    const startTimeB = Temporal.PlainTime.from(b.startTime);
    return Temporal.PlainTime.compare(startTimeA, startTimeB);
  });

  // Find the current or next schedule entry
  const currentOrNextSchedule =
    sortedEntries.find((entry) => {
      const entryDay = dayToNumber(entry.day);
      const entryEndTime = Temporal.PlainTime.from(entry.endTime);

      if (entryDay === currentDayOfWeek) {
        // Return if it's currently open or starts later today
        return Temporal.PlainTime.compare(currentTime, entryEndTime) < 0;
      }

      // Return if it's a future day
      return entryDay > currentDayOfWeek;
    }) ?? sortedEntries[0]; // Wrap around to first entry if nothing found

  if (!currentOrNextSchedule) {
    throw new Error('No schedule found');
  }

  const isCurrentlyOpen =
    dayToNumber(currentOrNextSchedule.day) === currentDayOfWeek &&
    Temporal.PlainTime.compare(currentTime, Temporal.PlainTime.from(currentOrNextSchedule.endTime)) < 0 &&
    Temporal.PlainTime.compare(currentTime, Temporal.PlainTime.from(currentOrNextSchedule.startTime)) >= 0;

  return {
    currentOrNextSchedule: currentOrNextSchedule,
    hours: {
      starts: currentOrNextSchedule.startTime,
      ends: currentOrNextSchedule.endTime,
      day: currentOrNextSchedule.day,
    },
    isCurrentlyOpen,
    isOpenToday: currentDayOfWeek === dayToNumber(currentOrNextSchedule.day),
    isOpenTomorrow: currentDayOfWeek + 1 === dayToNumber(currentOrNextSchedule.day),
  };
};
